import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import {
  Box,
  Button,
  Typography,
  Modal,
  Alert,
  Card,
  CardContent,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Backdrop
} from '@mui/material';
import {
  QrCode2,
  Close,
  Download,
  Share,
  ContentCopy,
  AccessTime,
  Person,
  CheckCircle
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  maxHeight: '90vh',
  overflow: 'auto',
  position: 'relative',
}));

const QRContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[300]}`,
  margin: theme.spacing(2, 0),
}));

const QRImageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[2],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CountdownBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.warning.light,
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const QRCodeGeneratorModal = ({
  open,
  onClose,
  reservation,
  type, // 'pickup' ou 'return'
  onGenerate,
  loading = false
}) => {
  const { t } = useTranslation('dashboard');
  const [qrImage, setQrImage] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  const isPickup = type === 'pickup';
  const title = isPickup ? t('reservationDetails.qrGenerator.titles.pickup') : t('reservationDetails.qrGenerator.titles.return');
  const userType = isPickup ? t('reservationDetails.qrGenerator.roles.renter') : t('reservationDetails.qrGenerator.roles.owner');
  const actionText = isPickup ? t('reservationDetails.qrGenerator.actions.pickup') : t('reservationDetails.qrGenerator.actions.return');
  const code = isPickup ? reservation?.client_code : reservation?.owner_code;
  
  // Déterminer si le code doit être visible selon la sécurité
  const shouldShowCode = () => {
    if (isPickup) {
      // Code pickup : toujours visible côté renter, visible côté owner après validation
      return true; // Le générateur est toujours celui qui possède le code
    } else {
      // Code return : toujours visible côté owner, visible côté renter après validation  
      return true; // Le générateur est toujours celui qui possède le code
    }
  };
  
  const isCodeVisible = shouldShowCode();

  // Countdown timer
  useEffect(() => {
    let interval;
    if (expiresAt) {
      interval = setInterval(() => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diff = Math.max(0, expiry - now);
        setTimeLeft(Math.floor(diff / 1000));
        
        if (diff <= 0) {
          setQrImage('');
          setExpiresAt(null);
          setError(t('reservationDetails.qrGenerator.messages.expired'));
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [expiresAt]);

  // Generate QR code on open
  useEffect(() => {
    if (open && !qrImage && !generating) {
      handleGenerate();
    }
  }, [open]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    
    try {
      const result = await onGenerate(reservation.id, type);
      
      console.log('QR API Result:', result); // Debug log
      
      if (result.success) {
        // Générer l'image QR côté client avec les données reçues
        const qrDataString = typeof result.data.qrData === 'string' 
          ? result.data.qrData 
          : JSON.stringify(result.data.qrData);
        
        console.log('QR Data to encode:', qrDataString); // Debug log
        const qrImageData = await QRCode.toDataURL(qrDataString, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        
        setQrImage(qrImageData);
        setExpiresAt(result.data.expiresAt || result.data.expiry);
      } else {
        setError(result.error || t('reservationDetails.qrGenerator.messages.generationError'));
      }
    } catch (err) {
      setError(t('reservationDetails.qrGenerator.messages.generationError'));
      console.error('QR Generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!qrImage) return;
    
    const link = document.createElement('a');
    link.href = qrImage;
    link.download = `qr-${type}-${reservation.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(t('reservationDetails.qrGenerator.messages.downloaded'));
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code.toString());
    toast.success(t('reservationDetails.qrGenerator.messages.codeCopied'));
  };

  const handleShare = async () => {
    if (navigator.share && qrImage) {
      try {
        // Convert base64 to blob
        const response = await fetch(qrImage);
        const blob = await response.blob();
        const file = new File([blob], `qr-${type}-${reservation.id}.png`, { type: 'image/png' });
        
        await navigator.share({
          title: `QR Code ${title}`,
          text: `QR code pour ${actionText} la réservation`,
          files: [file]
        });
      } catch (err) {
        console.error('Share failed:', err);
        handleDownload(); // Fallback to download
      }
    } else {
      handleDownload(); // Fallback to download
    }
  };

  const formatTimeLeft = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    // Empêcher la fermeture pendant la génération
    if (generating) return;
    
    setQrImage('');
    setExpiresAt(null);
    setError('');
    onClose();
  };

  return (
    <StyledModal
      open={open}
      onClose={generating ? null : handleClose}
      aria-labelledby="qr-generator-modal"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { 
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          // Éviter les clignotements pendant la génération
          transition: 'opacity 0.3s ease-in-out'
        }
      }}
    >
      <StyledCard>
        <CardContent>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              {title}
            </Typography>
            <IconButton onClick={handleClose} disabled={generating}>
              <Close />
            </IconButton>
          </Box>

          {/* Reservation Info */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('reservationDetails.qrGenerator.labels.reservation')}:</strong> {reservation?.listing?.title}<br/>
              <strong>{t('reservationDetails.qrGenerator.labels.role')}:</strong> {userType}<br/>
              <strong>{t('reservationDetails.qrGenerator.labels.action')}:</strong> {actionText} {t('reservationDetails.qrGenerator.labels.object')}
            </Typography>
          </Alert>

          {/* QR Code Section */}
          {generating ? (
            <QRContainer>
              <CircularProgress size={60} />
              <Typography variant="body2" color="text.secondary" mt={2}>
                {t('reservationDetails.qrGenerator.messages.generating')}
              </Typography>
            </QRContainer>
          ) : qrImage ? (
            <QRContainer>
              <Typography variant="body1" fontWeight="bold" mb={2}>
                {t('reservationDetails.qrGenerator.instructions.present')}
              </Typography>
              
              <QRImageContainer>
                <img 
                  src={qrImage} 
                  alt="QR Code" 
                  style={{ width: 200, height: 200 }}
                />
              </QRImageContainer>

              {timeLeft > 0 && (
                <CountdownBox>
                  <AccessTime fontSize="small" />
                  <Typography variant="body2">
                    {t('reservationDetails.qrGenerator.labels.expiresIn')}: {formatTimeLeft(timeLeft)}
                  </Typography>
                </CountdownBox>
              )}

              {timeLeft <= 0 && expiresAt && (
                <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
                  {t('reservationDetails.qrGenerator.messages.expiredGenerate')}
                </Alert>
              )}
            </QRContainer>
          ) : (
            <QRContainer>
              <QrCode2 sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={generating}
                startIcon={generating ? <CircularProgress size={20} /> : <QrCode2 />}
              >
                {generating ? t('reservationDetails.qrGenerator.buttons.generating') : t('reservationDetails.qrGenerator.buttons.generate')}
              </Button>
            </QRContainer>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Fallback Code Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('reservationDetails.qrGenerator.labels.backupCode')}
            </Typography>
            {isCodeVisible ? (
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Chip 
                  label={code} 
                  variant="outlined"
                  size="large"
                  sx={{ 
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.2rem'
                  }}
                />
                <IconButton onClick={handleCopyCode} size="small">
                  <ContentCopy />
                </IconButton>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('reservationDetails.qrGenerator.messages.codeMasked')}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('reservationDetails.qrGenerator.instructions.backupCode')}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={1} mt={3}>
            {qrImage && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownload}
                  size="small"
                >
                  {t('reservationDetails.qrGenerator.buttons.download')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Share />}
                  onClick={handleShare}
                  size="small"
                >
                  {t('reservationDetails.qrGenerator.buttons.share')}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              disabled={loading}
            >
              {t('reservationDetails.qrGenerator.buttons.close')}
            </Button>
          </Box>

          {/* Instructions */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('reservationDetails.qrGenerator.instructions.title')}:</strong><br/>
              {t('reservationDetails.qrGenerator.instructions.step1')}<br/>
              {t('reservationDetails.qrGenerator.instructions.step2', { action: isPickup ? 'pickup' : 'return' })}<br/>
              {t('reservationDetails.qrGenerator.instructions.step3')}
            </Typography>
          </Alert>
        </CardContent>
      </StyledCard>
    </StyledModal>
  );
};

export default QRCodeGeneratorModal;
